import {getTestProps} from '@benchling/js-test-utils';
import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  getAvailableStructurePredictionModels,
  getModelDisplayName,
  isPreviewModel,
  modelEgressesData,
  supportsMSA,
} from 'model-hub/scientific-models/ScientificModelConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {listToReadable} from 'util/NameUtil';

import MSAOptions from './MSAOptions';

interface ModelSelectorProps {
  selectedModel: string;
  onModelChange: (model: string) => void;
  msaEnabled?: boolean;
  onMSAEnabledChange?: (enabled: boolean) => void;
  disabled?: boolean;
  className?: string;
}

function PreviewBadge(): JSX.Element {
  return <Tag size={TagSize.SMALL} color={TagColor.PURPLE} text="PREVIEW" />;
}

export default function ModelSelector({
  selectedModel,
  onModelChange,
  msaEnabled = supportsMSA(selectedModel),
  onMSAEnabledChange,
  disabled = false,
  className,
}: ModelSelectorProps): JSX.Element {
  // Get available models based on feature flags
  const configEnabledModels = getAvailableStructurePredictionModels();
  const msaSupportedModelNames = configEnabledModels
    .filter((model) => supportsMSA(model))
    .map((model) => getModelDisplayName(model));
  const msaSupportedModelNamesCopy = listToReadable(msaSupportedModelNames);
  const msaSupportedModelVerbCopy = msaSupportedModelNames.length === 1 ? 'uses an MSA' : 'use MSAs';
  const msaSupportedCopy =
    msaSupportedModelNames.length > 0
      ? `${msaSupportedModelNamesCopy} ${msaSupportedModelVerbCopy}. Support for MSAs for all other models is coming soon.`
      : 'Support for MSAs is coming soon.';

  return (
    <div className={className} {...getTestProps({component: 'ModelSelector', element: 'root'})}>
      <div className={css(styles.label)}>Model</div>
      <SelectDropdown
        displayName={
          <div className={css(styles.rowContent)}>
            <span>{getModelDisplayName(selectedModel)}</span>
          </div>
        }
        disabled={disabled}
        {...getTestProps({component: 'ModelSelector', element: 'dropdown'})}
        menu={
          <Menu width={MenuWidth.FULL} value={selectedModel}>
            {configEnabledModels.map((model) => (
              <Option
                key={model}
                value={model}
                onSelect={() => {
                  onModelChange(model);
                }}
              >
                <div className={css(styles.rowContent)}>
                  <span>{getModelDisplayName(model)}</span>
                  {isPreviewModel(model) && <PreviewBadge />}
                </div>
              </Option>
            ))}
          </Menu>
        }
      />
      <div className={css(styles.helpText)}>
        <MSAOptions model={selectedModel} msaEnabled={msaEnabled} onMSAEnabledChange={onMSAEnabledChange} />
        <span>
          MSAs (multiple sequence alignments) increase accuracy by providing evolutionary context to the
          structure prediction model, but will add time to the prediction. {msaSupportedCopy}
        </span>
        {!modelEgressesData(selectedModel) && (
          <>
            <br />
            <br />
            <span>{getModelDisplayName(selectedModel)} is run on Benchling's infrastructure.</span>
          </>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  label: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
    marginBottom: '6px',
    color: GRAY_DARKER,
  },
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
    maxWidth: '600px',
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  previewBadge: {
    marginLeft: '8px',
  },
});
